import React, { useState, useEffect } from 'react';
import { 
  DollarSign,
  Edit3,
  Save,
  X,
  Calendar,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { 
  doc,
  updateDoc,
  serverTimestamp,
  addDoc,
  collection
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useRealtime } from '../../contexts/RealtimeContext';

import toast from 'react-hot-toast';

const TeacherSalaryManagement = ({ teacher, onClose, onUpdate }) => {
  const { currentUser, userProfile } = useRealtime();
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);

  // Debug logging
  useEffect(() => {
    console.log('TeacherSalaryManagement - Teacher:', teacher);
    console.log('TeacherSalaryManagement - Current User:', currentUser);
    console.log('TeacherSalaryManagement - User Profile:', userProfile);
    console.log('TeacherSalaryManagement - Teacher ID:', teacher?.id);
    console.log('TeacherSalaryManagement - Teacher CoachingId:', teacher?.coachingId);
    console.log('TeacherSalaryManagement - User CoachingId:', userProfile?.coachingId);
    console.log('TeacherSalaryManagement - User Role:', userProfile?.role);
  }, [teacher, currentUser, userProfile]);
  const [salaryData, setSalaryData] = useState({
    baseSalary: teacher?.teacherProfile?.salary?.baseSalary || 0,
    currency: teacher?.teacherProfile?.salary?.currency || 'INR',
    paymentSchedule: teacher?.teacherProfile?.salary?.paymentSchedule || 'monthly',
    ratePerClass: teacher?.teacherProfile?.salary?.ratePerClass || 0,
    bonuses: teacher?.teacherProfile?.salary?.bonuses || 0,
    deductions: teacher?.teacherProfile?.salary?.deductions || 0,
    effectiveFrom: teacher?.teacherProfile?.salary?.effectiveFrom || new Date().toISOString().split('T')[0],
    paymentMethod: teacher?.teacherProfile?.salary?.paymentMethod || 'bank_transfer',
    bankDetails: {
      accountNumber: teacher?.teacherProfile?.salary?.bankDetails?.accountNumber || '',
      ifscCode: teacher?.teacherProfile?.salary?.bankDetails?.ifscCode || '',
      bankName: teacher?.teacherProfile?.salary?.bankDetails?.bankName || '',
      accountHolderName: teacher?.teacherProfile?.salary?.bankDetails?.accountHolderName || teacher?.displayName || ''
    }
  });

  const handleSalaryUpdate = async () => {
    if (!salaryData.baseSalary || salaryData.baseSalary <= 0) {
      toast.error('Please enter a valid base salary');
      return;
    }

    if (!teacher?.id) {
      toast.error('Teacher ID is missing');
      return;
    }

    if (!currentUser?.uid) {
      toast.error('User not authenticated');
      return;
    }

    // Check if teacher has coachingId
    if (!teacher.coachingId) {
      toast.error('Teacher is not associated with any coaching center');
      return;
    }

    // Check user permissions
    if (!userProfile) {
      toast.error('User profile not loaded. Please refresh and try again.');
      return;
    }

    const isAdmin = userProfile.role === 'admin' || userProfile.role === 'superadmin';
    const isCoachingAdmin = userProfile.role === 'coaching_admin' && userProfile.coachingId === teacher.coachingId;

    if (!isAdmin && !isCoachingAdmin) {
      toast.error('You do not have permission to update teacher salaries');
      return;
    }

    setLoading(true);
    try {
      console.log('Updating salary for teacher:', teacher.id);
      console.log('Salary data:', salaryData);
      console.log('Current user:', currentUser?.uid);

      // First, try a simple update to test permissions
      const testUpdate = {
        updatedAt: serverTimestamp()
      };

      console.log('Testing basic update permission...');
      await updateDoc(doc(db, 'users', teacher.id), testUpdate);
      console.log('Basic update successful, proceeding with salary update...');

      // Now do the full salary update
      const updateData = {
        'teacherProfile.salary': {
          ...salaryData,
          lastUpdated: serverTimestamp(),
          updatedBy: currentUser?.uid
        },
        updatedAt: serverTimestamp()
      };

      console.log('Full update data:', updateData);

      await updateDoc(doc(db, 'users', teacher.id), updateData);
      console.log('Teacher salary updated successfully');

      // Log salary change (optional, don't fail if this fails)
      try {
        await addDoc(collection(db, 'salaryHistory'), {
          teacherId: teacher.id,
          teacherName: teacher.displayName || teacher.email,
          teacherEmail: teacher.email,
          previousSalary: teacher?.teacherProfile?.salary?.baseSalary || 0,
          newSalary: salaryData.baseSalary,
          changes: salaryData,
          updatedBy: currentUser?.uid,
          updatedAt: serverTimestamp(),
          coachingId: teacher.coachingId
        });
        console.log('Salary history logged successfully');
      } catch (historyError) {
        console.warn('Failed to log salary history (non-critical):', historyError);
        // Don't fail the whole operation if history logging fails
      }

      toast.success('Salary updated successfully');
      setEditMode(false);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating salary:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      // More specific error messages
      if (error.code === 'permission-denied') {
        toast.error('Permission denied. You may not have the required permissions to update teacher salary.');
      } else if (error.code === 'not-found') {
        toast.error('Teacher not found. Please refresh and try again.');
      } else if (error.code === 'invalid-argument') {
        toast.error('Invalid data provided. Please check all fields.');
      } else if (error.code === 'failed-precondition') {
        toast.error('Database operation failed. Please try again.');
      } else if (error.message?.includes('network')) {
        toast.error('Network error. Please check your connection and try again.');
      } else {
        toast.error(`Failed to update salary: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: salaryData.currency
    }).format(amount || 0);
  };

  const calculateNetSalary = () => {
    return (salaryData.baseSalary || 0) + (salaryData.bonuses || 0) - (salaryData.deductions || 0);
  };



  // Show error if teacher is not provided
  if (!teacher) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Error
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Teacher information is missing. Please try again.
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <DollarSign className="w-6 h-6 text-green-600" />
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  Salary Management
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {teacher?.displayName || teacher?.email}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {!editMode && (
                <button
                  onClick={() => setEditMode(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Edit3 className="w-4 h-4 inline mr-1" />
                  Edit Salary
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Current Salary Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 dark:text-blue-400">Base Salary</p>
                  <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                    {formatCurrency(salaryData.baseSalary)}
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-blue-500" />
              </div>
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 dark:text-green-400">Net Salary</p>
                  <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                    {formatCurrency(calculateNetSalary())}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </div>

            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 dark:text-purple-400">Per Class Rate</p>
                  <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                    {formatCurrency(salaryData.ratePerClass)}
                  </p>
                </div>
                <Clock className="w-8 h-8 text-purple-500" />
              </div>
            </div>
          </div>

          {/* Salary Details Form */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Salary Details
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Base Salary *
                </label>
                <input
                  type="number"
                  value={salaryData.baseSalary}
                  onChange={(e) => setSalaryData(prev => ({ ...prev, baseSalary: parseFloat(e.target.value) || 0 }))}
                  disabled={!editMode}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
                  placeholder="Enter base salary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Rate per Class
                </label>
                <input
                  type="number"
                  value={salaryData.ratePerClass}
                  onChange={(e) => setSalaryData(prev => ({ ...prev, ratePerClass: parseFloat(e.target.value) || 0 }))}
                  disabled={!editMode}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
                  placeholder="Rate per class"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Bonuses
                </label>
                <input
                  type="number"
                  value={salaryData.bonuses}
                  onChange={(e) => setSalaryData(prev => ({ ...prev, bonuses: parseFloat(e.target.value) || 0 }))}
                  disabled={!editMode}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
                  placeholder="Additional bonuses"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Deductions
                </label>
                <input
                  type="number"
                  value={salaryData.deductions}
                  onChange={(e) => setSalaryData(prev => ({ ...prev, deductions: parseFloat(e.target.value) || 0 }))}
                  disabled={!editMode}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
                  placeholder="Deductions (if any)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Payment Schedule
                </label>
                <select
                  value={salaryData.paymentSchedule}
                  onChange={(e) => setSalaryData(prev => ({ ...prev, paymentSchedule: e.target.value }))}
                  disabled={!editMode}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
                >
                  <option value="monthly">Monthly</option>
                  <option value="weekly">Weekly</option>
                  <option value="per_class">Per Class</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Effective From
                </label>
                <input
                  type="date"
                  value={salaryData.effectiveFrom}
                  onChange={(e) => setSalaryData(prev => ({ ...prev, effectiveFrom: e.target.value }))}
                  disabled={!editMode}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
                />
              </div>
            </div>
          </div>

          {/* Bank Details */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Bank Details
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Account Holder Name
                </label>
                <input
                  type="text"
                  value={salaryData.bankDetails.accountHolderName}
                  onChange={(e) => setSalaryData(prev => ({ 
                    ...prev, 
                    bankDetails: { ...prev.bankDetails, accountHolderName: e.target.value }
                  }))}
                  disabled={!editMode}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
                  placeholder="Account holder name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Account Number
                </label>
                <input
                  type="text"
                  value={salaryData.bankDetails.accountNumber}
                  onChange={(e) => setSalaryData(prev => ({ 
                    ...prev, 
                    bankDetails: { ...prev.bankDetails, accountNumber: e.target.value }
                  }))}
                  disabled={!editMode}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
                  placeholder="Bank account number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  IFSC Code
                </label>
                <input
                  type="text"
                  value={salaryData.bankDetails.ifscCode}
                  onChange={(e) => setSalaryData(prev => ({ 
                    ...prev, 
                    bankDetails: { ...prev.bankDetails, ifscCode: e.target.value.toUpperCase() }
                  }))}
                  disabled={!editMode}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
                  placeholder="IFSC Code"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Bank Name
                </label>
                <input
                  type="text"
                  value={salaryData.bankDetails.bankName}
                  onChange={(e) => setSalaryData(prev => ({ 
                    ...prev, 
                    bankDetails: { ...prev.bankDetails, bankName: e.target.value }
                  }))}
                  disabled={!editMode}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
                  placeholder="Bank name"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {editMode && (
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setEditMode(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSalaryUpdate}
                disabled={loading}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50 transition-colors"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 inline mr-1" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeacherSalaryManagement;